// THE SITE'S POP-UP: ONE MODAL CARD OVER A DIMMED PAGE (R251; shared since R-pending-web-free-first).

import { useEffect, useRef, type ReactNode, type RefObject } from "react";

/** What Tab can reach inside the pop-up. */
const TAB_STOPS = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * A POP-UP (R251): a dimmed backdrop over the page and one card on it, a real modal dialog. On opening the keyboard goes
 * to its heading; Tab and Shift+Tab stay inside it; Escape does what its own close does (the credit sheet's "Not yet", and
 * not while that is disabled — the spend is out; the free check's "Close"); on closing the keyboard goes back where it was,
 * or to `returnTo` when that is gone. The page behind does not scroll while it is open.
 *
 * Moved here unchanged from `Checkout.tsx` (R-pending-web-free-first), where it was the credit sheet's alone, so the free
 * check's pop-up is the same pop-up rather than a second one. `className` adds to the card's own classes: the free check's
 * is a full-height sheet on a phone (`styles/app.css`, `.sheet-full`).
 */
export function PopUp({
  labelledBy,
  kind,
  heading,
  onEscape,
  returnTo,
  className,
  children,
}: {
  labelledBy: string;
  /** Which sheet this is, on the element (`data-sheet`), as the inline sheets carry it. */
  kind: string;
  heading: RefObject<HTMLHeadingElement | null>;
  /** The sheet's own close, or null while it is disabled. */
  onEscape: (() => void) | null;
  returnTo: RefObject<HTMLElement | null>;
  className?: string;
  children: ReactNode;
}) {
  const card = useRef<HTMLElement>(null);
  const escape = useRef(onEscape);
  useEffect(() => {
    escape.current = onEscape;
  });

  useEffect(() => {
    const active = document.activeElement;
    const before = active instanceof HTMLElement && active !== document.body ? active : null;
    document.body.classList.add("popup-open");
    heading.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (escape.current === null) return;
        e.preventDefault();
        escape.current();
        return;
      }
      const box = card.current;
      if (e.key !== "Tab" || box === null) return;
      const stops = [...box.querySelectorAll<HTMLElement>(TAB_STOPS)];
      const at = document.activeElement;
      const inside = at instanceof HTMLElement && box.contains(at);
      if (stops.length === 0) {
        e.preventDefault();
        heading.current?.focus();
        return;
      }
      const first = stops[0];
      const last = stops[stops.length - 1];
      if (e.shiftKey && (!inside || at === first || at === heading.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!inside || at === last)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("popup-open");
      const back = before !== null && before.isConnected ? before : returnTo.current;
      back?.focus({ preventScroll: true });
    };
  }, [heading, returnTo]);

  return (
    <div className="alert-backdrop popup-backdrop" data-popup={kind}>
      <section
        ref={card}
        className={className === undefined ? "sheet sheet-popup" : `sheet sheet-popup ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        data-sheet={kind}
      >
        {children}
      </section>
    </div>
  );
}
